import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import type { BufferedChangeset } from 'ember-changeset/types';
import AdditionalInformations from 'parog-web/components/bio-exam-results/edition-form/additional-informations';
import Introduction from 'parog-web/components/bio-exam-results/edition-form/introduction';
import ResultsSection from 'parog-web/components/bio-exam-results/edition-form/results-section';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import type { ToastMessage } from 'parog-web/components/forc/abstract/journal-event-based-component';
import DraftManager from 'parog-web/components/forc/draft-manager';
import Provider from 'parog-web/components/forc/provider';
import PublicComment from 'parog-web/components/layout/medics-web/public-comment';
import NotificationBioExamJe from 'parog-web/components/notification/bio-exam-je';
import BioExamResultsReceivedJournalEvent from 'parog-web/models/bio-exam-results-received-journal-event';
import type BioExamObservation from 'parog-web/models/loinc';
import type Patient from 'parog-web/models/patient';
import type SemeiaSessionService from 'parog-web/services/session';
import bioExamResultsFilter from './edition-form/bio-exam-results-config-filter';
import type { ExamsByGroup } from './utils/groupedBioExams';
import { getGroupedBioExams } from './utils/groupedBioExams';

interface Args extends ForcBaseArgs {
  changeset: BufferedChangeset;
  model: BioExamResultsReceivedJournalEvent;
  patient: Patient;
}

export default class BioExamResultsEditionFormComponent extends CrudJournalEventBaseComponent<
  BioExamResultsReceivedJournalEvent,
  Args
> {
  static override forcDisplayName = 'Résultats biologiques';
  static override fullComponentId = 'bio-exam-results/edition-form';
  static override journalEventClass = BioExamResultsReceivedJournalEvent;

  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;

  @tracked isInvalid = false;

  bioExamResults = bioExamResultsFilter(
    this.patient,
    this.patient.therapeuticalArea?.defaultBioExams
  );
  override shouldAutoSaveDraft = true;

  override get changeset() {
    this.draft = new DraftManager(this);
    return this.args.changeset;
  }

  get groupedBioExamsObs(): ExamsByGroup {
    const sortedBioExams = this.bioExamResults
      .map(res => res.bioExamObservation as BioExamObservation)
      .sort(function (bioA, bioB) {
        return bioA.rank - bioB.rank;
      });

    return getGroupedBioExams(sortedBioExams);
  }

  override get isDirty() {
    // HACK: Refer to the associated commit for more details
    if (!this.changeset) return false;
    if (this.changeset.isInvalid) return false;

    return super.isDirty;
  }

  override get model(): BioExamResultsReceivedJournalEvent {
    // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
    return this.args.model;
  }

  override get patient() {
    return this.args.patient;
  }

  override get retrievedJournalEvent() {
    // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
    return this.model;
  }

  override get toastMessage(): ToastMessage | null {
    return this.session.patient
      ? {
          message: 'Vos résultats ont été envoyés.',
          title: 'Résultats de biologie'
        }
      : super.toastMessage;
  }

  onFileUploadClicked = (): void => {
    if (this.session.medic) {
      this.router.transitionTo({
        queryParams: {
          tab: 'upload-document'
        }
      });
    } else {
      this.router.transitionTo(
        `patient-web.protected.feature.component`,
        'document',
        'edition-form',
        {
          queryParams: {
            callbackUrl: '/patient-web/bio-results/edition'
          }
        }
      );
    }
  };

  override async save(): Promise<void> {
    this.changeset.set('normalized', true);
    this.changeset.set(
      'bioExamResultsAttributes',
      this.changeset.get('bioExamResults')
    );
    await super.save();
  }

  <template>
    <Provider
      @forcComponent={{this}}
      @isTextWidthLimitDisabled={{changesetGet this.changeset 'isFromHprim'}}
      @changeset={{this.changeset}}
    >
      <:content as |Form|>
        <Introduction @changeset={{this.changeset}} />

        {{#if this.session.medic}}
          <div class='text-grey'>À propos du bilan</div>
          <NotificationBioExamJe
            @bioNotifications={{this.model.notificationTriggeredJournalEvents}}
          />
        {{/if}}

        <AdditionalInformations
          @retrievedJournalEvent={{this.retrievedJournalEvent}}
          @validationStatus={{this.changeset.validationStatus}}
          @isFromHprim={{this.changeset.isFromHprim}}
        />

        <ResultsSection
          @groupedBioExamsObs={{this.groupedBioExamsObs}}
          @patient={{this.patient}}
          @changeset={{this.changeset}}
        />

        <div class='field'>
          <PublicComment
            @form={{Form}}
            @labelName='Commentaires'
            @propertyName='comments'
          />
        </div>
      </:content>

    </Provider>
  </template>
}
